import { useGetLicensesQuery } from '@/core/services/license'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { isStaff } from '@/utils/type-guards'
import { Patient } from '@/core/models/patient.model'
import { useMemo } from 'react'

const INS_POLICY_NAME = 'InsTeleserviceAccess'
const SUPPLEMENTARY_ATTRIBUTE_CODES_BLOCK_INSI = new Set<string>(['DOUTEUSE', 'FICTIVE'])

export type UseINSAccessResult = {
  /**
   * The connected staff holds a license carrying the INS teleservice policy. Only meant for the
   * settings tab that lets staff turn INS mode on — every other INS UI must use `isINSEnabled`.
   */
  hasLicense: boolean
  /** INS mode is active: licensed AND turned on in the staff's patient-management preferences. */
  isINSEnabled: boolean
  /** The INSi call is blocked for this patient (identity flagged as doubtful/fictitious). */
  isInsCallBlocked: boolean
}

/**
 * INS access is granted by license (an admin attaches INS_TELESERVICE to the staff members allowed
 * to use the teleservice); each licensed staff then opts in through the "Enable INS" preference,
 * which drives the INS fields/actions shown in the patient UI.
 */
export function useIsINSEnabled(patient?: Patient): UseINSAccessResult {
  const authUser = useAppSelector(selectAuthUser)
  const skip = !isStaff(authUser) || !authUser?.licenses?.length

  const { data: licenses } = useGetLicensesQuery({ id: authUser?.licenses ?? [] }, { skip })

  const hasLicense = licenses?.some((license) => license.policies?.some((policy) => policy.name === INS_POLICY_NAME)) ?? false
  const isPreferenceEnabled = isStaff(authUser) && !!authUser.managePatientPreferences?.insEnabled
  const isINSEnabled = hasLicense && isPreferenceEnabled

  const isInsCallBlocked = useMemo(
    () =>
      patient?.userInfo?.identitySupplementaryAttribute ? SUPPLEMENTARY_ATTRIBUTE_CODES_BLOCK_INSI.has(patient.userInfo.identitySupplementaryAttribute) : false,
    [patient]
  )

  return { hasLicense, isINSEnabled, isInsCallBlocked }
}
